/**
 * 관리 화면 진입점 — §10.0.
 *
 * `/admin` 은 있었지만 **어디서도 갈 수 없었다**. 관리자가 로그인해도 계정
 * 관리 화면을 찾지 못하니, 승인 대기 중인 사람은 계속 기다리기만 했다.
 * 기능이 있는데 문이 없으면 없는 것과 같다.
 *
 * 비관리자에게는 아무것도 그리지 않는다. 다만 이 숨김은 **편의지 통제가
 * 아니다** — 서버가 모든 관리 엔드포인트에서 관리자 여부를 다시 본다.
 * 주소를 직접 쳐서 들어와도 목록이 403 으로 막히고 화면이 그 사실을 말한다.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Button } from '@cupia/ui/button'
import { ShieldCheck } from 'lucide-react'
import { me } from '../lib/api'

export interface AdminLinkProps {
  /** 로그인한 계정이 관리자인가(`/api/me` 의 `is_admin`) */
  isAdmin: boolean | undefined
  size?: 'xs' | 'sm' | 'md' | 'lg'
  variant?: 'outline' | 'ghost' | 'secondary'
}

/**
 * 로그인한 계정이 관리자인지 스스로 확인한다.
 *
 * 이미 계정을 들고 있는 화면(랜딩)은 그 값을 `isAdmin` 으로 건네면 되고,
 * 계정을 모르는 화면(호스트 콘솔)은 이 훅을 쓴다 — 그 화면에 계정 상태
 * 관리를 새로 심지 않기 위해서다.
 *
 * 기본은 **아님**: 응답 전에 잠깐 보였다 사라지는 버튼이 더 나쁘다
 * (`useServerConfig` 와 같은 규율). 실패도 "아님" 으로 남는다.
 */
export function useIsAdmin(): boolean {
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    // 목 모드에서도 그대로 물어본다 — 목 플러그인이 `/api/me` 를 가로채므로
    // 실서버와 같은 경로를 탄다(랜딩 화면과 같은 방식).
    let cancelled = false
    void me()
      .then((acct) => {
        if (!cancelled) setIsAdmin(Boolean(acct.is_admin))
      })
      .catch(() => {
        // 비로그인(401)도 여기로 온다 — 진입점을 그리지 않는다.
      })
    return () => {
      cancelled = true
    }
  }, [])

  return isAdmin
}

export function AdminLink({
  isAdmin,
  size = 'sm',
  variant = 'ghost',
}: AdminLinkProps): React.ReactElement {
  if (!isAdmin) return <></>
  return (
    <Button asChild variant={variant} size={size} data-testid="admin-link">
      <Link to="/admin" title="계정 승인·권한 관리">
        <ShieldCheck className="h-3.5 w-3.5" />
        계정 관리
      </Link>
    </Button>
  )
}

/**
 * 계정을 모르는 화면(호스트 콘솔)용 — 스스로 확인하고 그린다.
 *
 * 회의 중 관리자가 승인 요청을 처리하려면 방을 닫고 나갈 필요가 없어야
 * 한다. 그 화면에 계정 상태 관리를 새로 심지 않으려고 진입점이 자기
 * 판단을 자기가 한다.
 */
export function SelfAdminLink(
  props: Omit<AdminLinkProps, 'isAdmin'>,
): React.ReactElement {
  const isAdmin = useIsAdmin()
  return <AdminLink {...props} isAdmin={isAdmin} />
}
